'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { PhotoGalleryForm, type PhotoGalleryFormRef } from '@/components/partials/photo-galleries'
import { ContentError, FormSkeleton } from '@/components/partials'
import { usePhotoGallery, useUpdatePhotoGallery } from '@/hooks'
import { type CreatePhotoGalleryParams } from '@/types/photo-gallery'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const photoGalleryFormRef = useRef<PhotoGalleryFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: photoGallery, isLoading, error } = usePhotoGallery(id)
  const { mutate: updatePhotoGallery, isPending } = useUpdatePhotoGallery()

  const handleSubmit = (data: CreatePhotoGalleryParams) => {
    updatePhotoGallery(
      { id, params: data },
      {
        onSuccess: async updatedPhotoGallery => {
          setIsRedirecting(true)
          queryClient.setQueryData(['photo-gallery', id], updatedPhotoGallery)
          await queryClient.invalidateQueries({ queryKey: ['photo-galleries'] })
          toast.success(t('updated_successfully'))
          router.push(`/galleries/photo-galleries/${updatedPhotoGallery.id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (photoGalleryFormRef.current) {
        photoGalleryFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/galleries/photo-galleries/${id}`)
  }

  if (isLoading || !photoGallery) {
    return <FormSkeleton fieldRows={1} showPictures />
  }

  if (error || !photoGallery) {
    return <ContentError error={error} listUrl="/galleries/photo-galleries" />
  }

  return (
    <PhotoGalleryForm
      ref={photoGalleryFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={photoGallery}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
